import React, { Component } from "react";
import { withStyles } from "@material-ui/core/styles";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import { batch } from "react-redux";
import produce from "immer";
import { setAutoFreeze } from "immer";

import Highcharts from "highcharts";
import HighchartsHeatmap from "highcharts/modules/heatmap";
import HighchartsReact from "highcharts-react-official";
import HighchartsColorAxis from "highcharts/modules/coloraxis";
import HighchartsPatternFill from "highcharts/modules/pattern-fill";

import {
  minSliceThickness,
  minRoiThickness,
  csiTypes,
} from "../../constants/constants";
import { swsPlotOptions } from "../../constants/highcharts/swsPlotOptions";
import { swsPlotColorBarOptions } from "../../constants/highcharts/swsPlotColorBarOptions";
import { velocitySlicePlotOptions } from "../../constants/highcharts/velocitySlicePlotOptions";
import { colors } from "../../constants/colors";
import { customTheme } from "../../constants/customTheme";
import WarningIcon from "@material-ui/icons/Warning";

// MUI Components
import Grid from "@material-ui/core/Grid";
import Slider from "@material-ui/core/Slider";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Switch from "@material-ui/core/Switch";
import Typography from "@material-ui/core/Typography";
import Accordion from "@material-ui/core/Accordion";
import AccordionDetails from "@material-ui/core/AccordionDetails";
import AccordionSummary from "@material-ui/core/AccordionSummary";
import ListSubheader from "@material-ui/core/ListSubheader";
import Divider from "@material-ui/core/Divider";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import Button from "@material-ui/core/Button";
import ListItem from "@material-ui/core/ListItem";
import ListItemIcon from "@material-ui/core/ListItemIcon";
import ListItemText from "@material-ui/core/ListItemText";
import { studyFixture } from "../../constants/Fixtures/studyFixture";
// Actions
import {
  setZValue,
  setDz,
  fetchSwsValueAndVelocitySlicePlot,
  fetchSwsPlotAndVelocitySlicePlot,
  setRoiPercentageSlice,
  updateLocationWideRoiReference,
  fetchSwsPlot,
  setVMax,
  changeGlobalVelocityColorbarChange,
  changeGlobalSwsColorbarChange,
} from "../../actions";

HighchartsHeatmap(Highcharts);
HighchartsColorAxis(Highcharts);
HighchartsPatternFill(Highcharts);
setAutoFreeze(false); // turns off immer's auto-freeze feature - (it was causing a bug when the number of data points being graphed changed)

const styles = {
  graphTitleContainer: {
    textAlign: "center",
  },
  graphTitle: {
    fontSize: "1.2rem",
  },
  graphContainer: {
    width: "auto",
  },
  graphControlsContainer: {
    padding: "20px",
  },
  divider: {
    width: "100%",
    marginTop: "10px",
    backgroundColor: colors.disabledColor,
  },
  graphSettingsSubheader: {
    height: "35px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "0.875rem",
    fontWeight: 400,
  },
  alertGrid: {
    padding: "0px 20px 0px 20px",
  },
  buttonPrimary: (props) => customTheme(props.theme).buttonPrimary,
  buttonOutlined: (props) => customTheme(props.theme).buttonOutlined,
  colorbarButtons: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-evenly",
  },
  graphSettingsGrid: {
    display: "flex",
    justifyContent: "center",
  },
  graphSettingsSubGrid: {
    flexDirection: "column",
    margin: "20px 0px 40px 0px",
    padding: "0px 20px 0px 20px",
    "& div": {
      flex: "1 1 auto",
    },
  },
  sliderDiv: {
    marginBottom: "10px",
    justifyContent: "space-between",
  },
  sliderContainer: {
    margin: "20px 20px 40px 20px",
    width: "75%",
  },
  sliderTitle: {
    marginBottom: "20px",
  },
  switch: {
    textAlign: "center",
    flexDirection: "row-revers",
    "& .MuiFormControlLabel-label": {
      fontSize: "1rem",
    },
  },
  listIcon: {
    ...customTheme().iconMedium,
    color: (props) => colors.warning,
  },
  listText: {
    "& span": {
      fontSize: "1rem",
    },
    "& p": {
      fontSize: "1rem",
    },
  },
};

class CsiPlot extends Component {
  constructor(props) {
    super(props);
    this.updateWindowDimensions = this.updateWindowDimensions.bind(this);
    this.state = {
      // initialize slider states with values from redux store
      swsColorBarRange: [
        Math.round(this.props.colorBar.yAxis.range[0]),
        Math.round(this.props.colorBar.yAxis.range[1]),
      ],
      bColorBarRange: [
        Math.round(this.props.colorBar.xAxis.range[0]),
        Math.round(this.props.colorBar.xAxis.range[1]),
      ],
      velocityColorbarValue: this.props.velocitySlicePlotColorBar
        ? this.props.velocitySlicePlotColorBar.range[1]
        : null,
      sliceWidthSliderValue: this.props.percentSliceWidth,
      startDragY: null, // keeps track of the mouse y position when a drag is initiated
      elementStartPosY: null, // keeps track of initial y position of element when a drag is initiated
      expandGraphSettings: false,
    };
  }

  updateWindowDimensions() {
    let newSwsWidth = window.innerWidth * 0.6 - 100;
    let newSwsHeight = newSwsWidth * 0.85;

    let newVelocitySliceWidth = window.innerWidth * 0.6 - 15; //15
    let newVelocitySliceHeight = newVelocitySliceWidth * 0.4;

    this.setState({
      ...this.state,
      windowSwsPlotHeight: newSwsHeight,
      windowSwsPlotWidth: newSwsWidth, // The result column is always 60% of the window. Based on window size calculate the graph size and always leave 20px gap on each side
      windowVeloctiySlicePlotHeight: newVelocitySliceHeight,
      windowVeloctiySlicePlotWidth: newVelocitySliceWidth, // The result column is always 60% of the window. Based on window size calculate the graph size and always leave 20px gap on each side
      colorBarVeloctiySliceHeight: newVelocitySliceHeight - 64, // 64 px is the height of the x-axis labels
    });
  }

  componentDidMount() {
    this.chartRefSwsPlot = React.createRef();
    this.chartRefVelocitySlicePlot = React.createRef();
    // this.initializeEventListeners();
    this.updateWindowDimensions();
    window.addEventListener("resize", this.updateWindowDimensions);
  }

  componentDidUpdate(prevProps) {
    // this.initializeEventListeners();

    // if sample changes, re-sync local state with redux values
    if (this.props.sample.id !== prevProps.sample.id) {
      this.setState({
        swsColorBarRange: [
          Math.round(this.props.colorBar.yAxis.range[0]),
          Math.round(this.props.colorBar.yAxis.range[1]),
        ],
        bColorBarRange: [
          Math.round(this.props.colorBar.xAxis.range[0]),
          Math.round(this.props.colorBar.xAxis.range[1]),
        ],
        velocityColorbarValue: this.props.velocitySlicePlotColorBar
          ? this.props.velocitySlicePlotColorBar.range[1]
          : null,
        sliceWidthSliderValue: this.props.percentSliceWidth,
      });
    }
  }

  initializeEventListeners() {
    // add eventListeners for plotband inteactions
    const plotBandROI = document.getElementsByClassName("plotBandROI")[0];
    const plotLineTop = document.getElementsByClassName("sliceRegionStart")[0];
    const plotLineBottom = document.getElementsByClassName("sliceRegionEnd")[0];

    if (
      plotBandROI &&
      plotLineTop &&
      plotLineBottom &&
      !this.props.sample.processing &&
      !this.props.locked &&
      this.props.enabled &&
      this.props.sample.dataAvailable &&
      this.props.sample.error.type !== "PROCESSING" // do not allow for graph changes if there was an error during processing
    ) {
      // Mouse Events
      plotBandROI.addEventListener("mousedown", this.handleRoiDown);
      plotLineTop.addEventListener("mousedown", this.handleTopLineDown);
      plotLineBottom.addEventListener("mousedown", this.handleBottomLineDown);
      // Touch Events
      plotBandROI.addEventListener("touchstart", this.handleRoiDownTouch);
      plotLineTop.addEventListener("touchstart", this.handleTopLineDownTouch);
      plotLineBottom.addEventListener(
        "touchstart",
        this.handleBottomLineDownTouch
      );
    }
  }

  componentWillUnmount() {
    window.removeEventListener("resize", this.updateWindowDimensions);
    // clean-up / remove all event listeners
    const plotBandROI = document.getElementsByClassName("plotBandROI")[0];
    const plotLineTop = document.getElementsByClassName("sliceRegionStart")[0];
    const plotLineBottom = document.getElementsByClassName("sliceRegionEnd")[0];

    if (plotBandROI && plotLineTop && plotLineBottom) {
      // Mouse Event
      plotBandROI.removeEventListener("mousedown", this.handleRoiDown);
      plotLineTop.removeEventListener("mousedown", this.handleTopLineDown);
      plotLineBottom.removeEventListener(
        "mousedown",
        this.handleBottomLineDown
      );
      // Touch Events
      plotBandROI.removeEventListener("touchend", this.handleRoiDownTouch);
      plotLineTop.removeEventListener("touchend", this.handleTopLineDownTouch);
      plotLineBottom.removeEventListener(
        "touchend",
        this.handleBottomLineDownTouch
      );
    }
  }

  ////////////////////////
  // CHART MOUSE EVENTS //
  ////////////////////////

  // mouse down on ROI plotBand
  handleRoiDown = (e) => {
    // console.log('ROI plotBand Down!')
    document.addEventListener("mousemove", this.handleRoiDrag);
    document.addEventListener("mouseup", this.handleRoiRelease);

    // save initial y value for startDragY
    const target = e.target.getBoundingClientRect();
    this.setState({
      startDragY: e.clientY,
      elementStartPosY: target.y + target.height / 2,
    });
  };

  // mouse down on top line region
  handleTopLineDown = (e) => {
    // console.log('Top Line Down')
    document.addEventListener("mousemove", this.handleTopLineDrag);
    document.addEventListener("mouseup", this.handleTopLineRelease);

    this.setState({
      startDragY: e.clientY, // save initial y value for startDragY
      elementStartPosY: e.target.getBoundingClientRect().y,
    });
  };

  // mouse down on bottom line region
  handleBottomLineDown = (e) => {
    // console.log('Bottom Line Down')
    document.addEventListener("mousemove", this.handleBottomLineDrag);
    document.addEventListener("mouseup", this.handleBottomLineRelease);

    this.setState({
      startDragY: e.clientY, // save initial y mouse value
      elementStartPosY: e.target.getBoundingClientRect().y,
    });
  };

  // touch down on ROI plotBand
  handleRoiDownTouch = (e) => {
    // console.log('ROI plotBand Down!')
    // e.preventDefault(); // prevent default scrolling and clicking action
    e.stopPropagation();
    e.stopImmediatePropagation();
    document
      .getElementsByClassName("plotBandROI")[0]
      .addEventListener("touchmove", this.handleRoiDragTouch);
    document
      .getElementsByClassName("plotBandROI")[0]
      .addEventListener("touchend", this.handleRoiReleaseTouch);

    // save initial y value for startDragY
    const target = e.target.getBoundingClientRect();
    this.setState({
      startDragY: e.changedTouches[0].clientY,
      elementStartPosY: target.y + target.height / 2,
    });
  };

  // touch down on top line region
  handleTopLineDownTouch = (e) => {
    // console.log('Top Line Down')
    // e.preventDefault(); // prevent default scrolling and clicking action
    e.stopPropagation();
    e.stopImmediatePropagation();
    document
      .getElementsByClassName("sliceRegionStart")[0]
      .addEventListener("touchmove", this.handleTopLineDragTouch);
    document
      .getElementsByClassName("sliceRegionStart")[0]
      .addEventListener("touchend", this.handleTopLineReleaseTouch);

    this.setState({
      startDragY: e.changedTouches[0].clientY, // save initial y value for startDragY
      elementStartPosY: e.target.getBoundingClientRect().y,
    });
  };

  // touch down on bottom line region
  handleBottomLineDownTouch = (e) => {
    // console.log('Bottom Line Down')
    // e.preventDefault(); // prevent default scrolling and clicking action
    e.stopPropagation();
    e.stopImmediatePropagation();
    document
      .getElementsByClassName("sliceRegionEnd")[0]
      .addEventListener("touchmove", this.handleBottomLineDragTouch);
    document
      .getElementsByClassName("sliceRegionEnd")[0]
      .addEventListener("touchend", this.handleBottomLineReleaseTouch);

    this.setState({
      startDragY: e.changedTouches[0].clientY, // save initial y mouse value
      elementStartPosY: e.target.getBoundingClientRect().y,
    });
  };

  handleRoiDrag = (e) => {
    // console.log("Dragging ROI PlotBand");
    // Drag interactive elements fluidly by directly manipulating the DOM
    // we avoid re-rendering highcharts for performance

    // select all elements that have to be moved
    const plotBandROI = document.getElementsByClassName("plotBandROI")[0];
    const sliceLineTop = document.getElementsByClassName("sliceLineStart")[0];
    const sliceLineBottom = document.getElementsByClassName("sliceLineEnd")[0];

    // select plot background
    const plotBkg = document
      .getElementById("swsHeatmap")
      .getElementsByClassName("highcharts-plot-background")[0];

    const plotBkgTop = plotBkg.getBoundingClientRect().top;
    const plotBkgBottom = plotBkg.getBoundingClientRect().bottom;

    const dMouseY = e.clientY - this.state.startDragY; // distance mouse has moved
    const newSliceLineTopY =
      this.state.elementStartPosY -
      this.calculateSliceThickness() / 2 +
      dMouseY;
    const newSliceLineBottomY =
      this.state.elementStartPosY +
      this.calculateSliceThickness() / 2 +
      dMouseY;
    // if new position will be withing the bounds of the graph
    if (newSliceLineTopY > plotBkgTop && newSliceLineBottomY < plotBkgBottom) {
      // translate all elements by dMouseY
      plotBandROI.setAttribute("transform", `translate(0, ${dMouseY})`);
      sliceLineTop.setAttribute("transform", `translate(0, ${dMouseY})`);
      sliceLineBottom.setAttribute("transform", `translate(0, ${dMouseY})`);
    }
  };

  handleTopLineDrag = (e) => {
    // console.log('Top Line Drag')
    // select all elements that have to be moved
    const plotBandROI = document.getElementsByClassName("plotBandROI")[0];
    const sliceLineTop = document.getElementsByClassName("sliceLineStart")[0];
    const sliceRegionTop =
      document.getElementsByClassName("sliceRegionStart")[0];
    const sliceLineBottom = document.getElementsByClassName("sliceLineEnd")[0];

    // select plot background
    const plotBkg = document
      .getElementById("swsHeatmap")
      .getElementsByClassName("highcharts-plot-background")[0];

    const plotBkgTop = plotBkg.getBoundingClientRect().top;
    const originalSliceThickness = this.convertToPixels(this.props.sample.dz);

    const dMouseY = e.clientY - this.state.startDragY; // distance mouse has moved
    const newSliceLineTopY = this.state.elementStartPosY + dMouseY;
    const sliceLineBottomY = sliceLineBottom.getBoundingClientRect().y;
    const scaleFactor = this.calculateSliceThickness() / originalSliceThickness;

    const newSliceThicknessMM = this.convertToChartUnits(
      sliceLineBottomY - newSliceLineTopY
    );

    const newRoiThicknessMM =
      (newSliceThicknessMM * this.props.percentSliceWidth) / 100;
    // Determine if DOM should update
    if (
      newSliceLineTopY > plotBkgTop && // below chart top
      newSliceLineTopY < sliceLineBottomY && // above bottom line
      newSliceThicknessMM >= minSliceThickness && // greater than min slice thickness
      newRoiThicknessMM >= minRoiThickness // greater than min ROI thickness
    ) {
      // translate all elements by dMouseY
      sliceLineTop.setAttribute("transform", `translate(0, ${dMouseY})`);
      sliceRegionTop.setAttribute("transform", `translate(0, ${dMouseY})`);

      // keep plotBand in center and re-scale it
      plotBandROI.style.transform = `translate(0, ${
        dMouseY / 2
      }px) scale(1, ${scaleFactor})`;
    }
  };

  handleBottomLineDrag = (e) => {
    // console.log('Bottom Line Drag')
    // select all elements that have to be moved
    const plotBandROI = document.getElementsByClassName("plotBandROI")[0];
    const sliceLineBottom = document.getElementsByClassName("sliceLineEnd")[0];
    const sliceRegionBottom =
      document.getElementsByClassName("sliceRegionEnd")[0];
    const sliceLineTop = document.getElementsByClassName("sliceLineStart")[0];

    // select plot background
    const plotBkg = document
      .getElementById("swsHeatmap")
      .getElementsByClassName("highcharts-plot-background")[0];

    const plotBkgBottom = plotBkg.getBoundingClientRect().bottom;
    const originalSliceThickness = this.convertToPixels(this.props.sample.dz);

    const dMouseY = e.clientY - this.state.startDragY; // distance mouse has moved
    const newSliceLineBottomY = this.state.elementStartPosY + dMouseY; // calculate new position before commiting it to DOM (for conditionals)
    const sliceLineTopY = sliceLineTop.getBoundingClientRect().y;
    const scaleFactor = this.calculateSliceThickness() / originalSliceThickness;
    const newSliceThicknessMM = this.convertToChartUnits(
      newSliceLineBottomY - sliceLineTopY
    );
    const newRoiThicknessMM =
      (newSliceThicknessMM * this.props.percentSliceWidth) / 100;
    // Determine if DOM should update
    if (
      newSliceLineBottomY < plotBkgBottom && // above chart bottom
      newSliceLineBottomY > sliceLineTopY && // below top line
      newSliceThicknessMM >= minSliceThickness && // greater than min slice thickness
      newRoiThicknessMM >= minRoiThickness // greater than min ROI thickness
    ) {
      // translate all elements
      sliceLineBottom.setAttribute("transform", `translate(0, ${dMouseY})`);
      sliceRegionBottom.setAttribute("transform", `translate(0, ${dMouseY})`);

      // keep plotBand in center and re-scale it
      plotBandROI.style.transform = `translate(0, ${
        dMouseY / 2
      }px) scale(1, ${scaleFactor})`;
    }
  };

  handleRoiDragTouch = (e) => {
    // console.log("Dragging ROI PlotBand");
    e.preventDefault(); // prevent default scrolling and clicking action
    // select all elements that have to be moved
    const plotBandROI = document.getElementsByClassName("plotBandROI")[0];
    const sliceLineTop = document.getElementsByClassName("sliceLineStart")[0];
    const sliceLineBottom = document.getElementsByClassName("sliceLineEnd")[0];

    // select plot background
    const plotBkg = document
      .getElementById("swsHeatmap")
      .getElementsByClassName("highcharts-plot-background")[0];

    const plotBkgTop = plotBkg.getBoundingClientRect().top;
    const plotBkgBottom = plotBkg.getBoundingClientRect().bottom;

    const dMouseY = e.changedTouches[0].clientY - this.state.startDragY; // distance mouse has moved
    const newSliceLineTopY =
      this.state.elementStartPosY -
      this.calculateSliceThickness() / 2 +
      dMouseY;
    const newSliceLineBottomY =
      this.state.elementStartPosY +
      this.calculateSliceThickness() / 2 +
      dMouseY;

    // if new position will be withing the bounds of the graph
    if (newSliceLineTopY > plotBkgTop && newSliceLineBottomY < plotBkgBottom) {
      // translate all elements by dMouseY
      plotBandROI.setAttribute("transform", `translate(0, ${dMouseY})`);
      sliceLineTop.setAttribute("transform", `translate(0, ${dMouseY})`);
      sliceLineBottom.setAttribute("transform", `translate(0, ${dMouseY})`);
    }
  };

  handleTopLineDragTouch = (e) => {
    // console.log('Top Line Drag')
    e.preventDefault(); // prevent default scrolling and clicking action
    // select all elements that have to be moved
    const plotBandROI = document.getElementsByClassName("plotBandROI")[0];
    const sliceLineTop = document.getElementsByClassName("sliceLineStart")[0];
    const sliceRegionTop =
      document.getElementsByClassName("sliceRegionStart")[0];
    const sliceLineBottom = document.getElementsByClassName("sliceLineEnd")[0];

    // select plot background
    const plotBkg = document
      .getElementById("swsHeatmap")
      .getElementsByClassName("highcharts-plot-background")[0];

    const plotBkgTop = plotBkg.getBoundingClientRect().top;
    const originalSliceThickness = this.convertToPixels(this.props.sample.dz);

    const dMouseY = e.changedTouches[0].clientY - this.state.startDragY; // distance mouse has moved
    const newSliceLineTopY = this.state.elementStartPosY + dMouseY;
    const sliceLineBottomY = sliceLineBottom.getBoundingClientRect().y;
    const scaleFactor = this.calculateSliceThickness() / originalSliceThickness;
    const newSliceThicknessMM = this.convertToChartUnits(
      sliceLineBottomY - newSliceLineTopY
    );
    const newRoiThicknessMM =
      (newSliceThicknessMM * this.props.percentSliceWidth) / 100;

    // Determine if DOM should update
    if (
      newSliceLineTopY > plotBkgTop && // below chart top
      newSliceLineTopY < sliceLineBottomY && // above bottom line
      newSliceThicknessMM >= minSliceThickness && // greater than min slice thickness
      newRoiThicknessMM >= minRoiThickness // greater than min ROI thickness
    ) {
      // translate all elements by dMouseY
      sliceLineTop.setAttribute("transform", `translate(0, ${dMouseY})`);
      sliceRegionTop.setAttribute("transform", `translate(0, ${dMouseY})`);

      // keep plotBand in center and re-scale it
      plotBandROI.style.transform = `translate(0, ${
        dMouseY / 2
      }px) scale(1, ${scaleFactor})`;
    }
  };

  handleBottomLineDragTouch = (e) => {
    // console.log('Bottom Line Drag')
    e.preventDefault(); // prevent default scrolling and clicking action
    // select all elements that have to be moved
    const plotBandROI = document.getElementsByClassName("plotBandROI")[0];
    const sliceLineBottom = document.getElementsByClassName("sliceLineEnd")[0];
    const sliceRegionBottom =
      document.getElementsByClassName("sliceRegionEnd")[0];
    const sliceLineTop = document.getElementsByClassName("sliceLineStart")[0];

    // select plot background
    const plotBkg = document
      .getElementById("swsHeatmap")
      .getElementsByClassName("highcharts-plot-background")[0];

    const plotBkgBottom = plotBkg.getBoundingClientRect().bottom;
    const originalSliceThickness = this.convertToPixels(this.props.sample.dz);

    const dMouseY = e.changedTouches[0].clientY - this.state.startDragY; // distance mouse has moved
    const newSliceLineBottomY = this.state.elementStartPosY + dMouseY; // calculate new position before commiting it to DOM (for conditionals)
    const sliceLineTopY = sliceLineTop.getBoundingClientRect().y;
    const scaleFactor = this.calculateSliceThickness() / originalSliceThickness;
    const newSliceThicknessMM = this.convertToChartUnits(
      newSliceLineBottomY - sliceLineTopY
    );
    const newRoiThicknessMM =
      (newSliceThicknessMM * this.props.percentSliceWidth) / 100;

    // Determine if DOM should update
    if (
      newSliceLineBottomY < plotBkgBottom && // above chart bottom
      newSliceLineBottomY > sliceLineTopY && // below top line
      newSliceThicknessMM >= minSliceThickness && // greater than min slice thickness
      newRoiThicknessMM >= minRoiThickness // greater than min ROI thickness
    ) {
      // translate all elements
      sliceLineBottom.setAttribute("transform", `translate(0, ${dMouseY})`);
      sliceRegionBottom.setAttribute("transform", `translate(0, ${dMouseY})`);

      // keep plotBand in center and re-scale it
      plotBandROI.style.transform = `translate(0, ${
        dMouseY / 2
      }px) scale(1, ${scaleFactor})`;
    }
  };

  handleRoiRelease = (e) => {
    // console.log('ROI plotBand Release!')
    document.removeEventListener("mousemove", this.handleRoiDrag);
    document.removeEventListener("mouseup", this.handleRoiRelease);

    // commit change to redux store
    this.props.setZValue(this.calculateNewZValue(), this.props.sample.id);
    this.resetRoiReference();
    this.props.fetchSwsValueAndVelocitySlicePlot(this.props.sample.id);
  };

  handleTopLineRelease = () => {
    // console.log('Top Line Release')
    document.removeEventListener("mousemove", this.handleTopLineDrag);
    document.removeEventListener("mouseup", this.handleTopLineRelease);

    let sliceDist = this.calculateSliceThickness();
    let newDz = this.convertToChartUnits(sliceDist);

    batch(() => {
      // commit changes to redux store
      this.props.setZValue(this.calculateNewZValue(), this.props.sample.id); // updates center of slice position (z)
      this.props.setDz(this.props.sample.id, newDz); // updates plotLine spacing
    });

    // request new SWS from server
    this.resetRoiReference();
    this.props.fetchSwsValueAndVelocitySlicePlot(this.props.sample.id);
  };

  handleBottomLineRelease = (e) => {
    // console.log('Bottom Line Release')
    document.removeEventListener("mousemove", this.handleBottomLineDrag);
    document.removeEventListener("mouseup", this.handleBottomLineRelease);

    let sliceDist = this.calculateSliceThickness();
    let newDz = this.convertToChartUnits(sliceDist);

    batch(() => {
      // commit changes to redux store
      let newzValue = this.calculateNewZValue();
      this.props.setZValue(newzValue, this.props.sample.id); // update center positioning
      this.props.setDz(this.props.sample.id, newDz); // update plotLine spacing
    });

    // request new SWS from server
    this.resetRoiReference();
    this.props.fetchSwsValueAndVelocitySlicePlot(this.props.sample.id);
  };

  handleRoiReleaseTouch = (e) => {
    // console.log('ROI plotBand Release!')
    e.preventDefault(); // prevent default scrolling and clicking action
    document.removeEventListener("touchmove", this.handleRoiDragTouch);
    document.removeEventListener("touchend", this.handleRoiReleaseTouch);

    // commit change to redux store
    this.props.setZValue(this.calculateNewZValue(), this.props.sample.id);
    // request new SWS from server
    this.resetRoiReference();
    this.props.fetchSwsValueAndVelocitySlicePlot(this.props.sample.id);
  };

  handleTopLineReleaseTouch = () => {
    // console.log('Top Line Release')
    document.removeEventListener("touchmove", this.handleTopLineDragTouch);
    document.removeEventListener("touchend", this.handleTopLineReleaseTouch);

    let sliceDist = this.calculateSliceThickness();
    let newDz = this.convertToChartUnits(sliceDist);

    batch(() => {
      // commit changes to redux store
      this.props.setZValue(this.calculateNewZValue(), this.props.sample.id); // updates center of slice position (z)
      this.props.setDz(this.props.sample.id, newDz); // updates plotLine spacing
    });

    // request new SWS from server
    this.resetRoiReference();
    this.props.fetchSwsValueAndVelocitySlicePlot(this.props.sample.id);
  };

  handleBottomLineReleaseTouch = (e) => {
    // console.log('Bottom Line Release')
    document.removeEventListener("touchmove", this.handleBottomLineDragTouch);
    document.removeEventListener("touchend", this.handleBottomLineReleaseTouch);

    let sliceDist = this.calculateSliceThickness();
    let newDz = this.convertToChartUnits(sliceDist);

    batch(() => {
      // commit changes to redux store
      this.props.setZValue(this.calculateNewZValue(), this.props.sample.id); // update center positioning
      this.props.setDz(this.props.sample.id, newDz); // update plotLine spacing
    });

    // request new SWS from server
    this.resetRoiReference();
    this.props.fetchSwsValueAndVelocitySlicePlot(this.props.sample.id);
  };

  //////////////////////
  // HELPER FUNCTIONS //
  //////////////////////

  // converts current chart DOM properties to new zIndex value
  calculateNewZValue = () => {
    // select plot background
    const plotBkgRect = document
      .getElementById("swsHeatmap")
      .getElementsByClassName("highcharts-plot-background")[0]
      .getBoundingClientRect();

    // retrieve new ROI plotBand position
    const plotBandRoiRect = document
      .getElementsByClassName("plotBandROI")[0]
      .getBoundingClientRect();
    const plotBandRoiPosY =
      plotBandRoiRect.y + plotBandRoiRect.height / 2 - plotBkgRect.top;

    const newZ =
      this.props.yAxis.range[0] + this.convertToChartUnits(plotBandRoiPosY);

    var closestZValue = this.props.zList.reduce((prev, curr) => {
      return Math.abs(curr - newZ) < Math.abs(prev - newZ) ? curr : prev;
    });
    return closestZValue;
  };

  // calculates pixel distance between top and bottom slice lines (DOM)
  calculateSliceThickness = () => {
    const sliceLineTopY = document
      .getElementsByClassName("sliceLineStart")[0]
      .getBoundingClientRect().y;
    const sliceLineBottomY = document
      .getElementsByClassName("sliceLineEnd")[0]
      .getBoundingClientRect().y;
    return sliceLineBottomY - sliceLineTopY;
  };

  // converts from pixel (DOM) units to highcharts units
  convertToChartUnits = (value) => {
    const plotBkgHeight = document
      .getElementById("swsHeatmap")
      .getElementsByClassName("highcharts-plot-background")[0]
      .getBoundingClientRect().height;

    const chartRangeY = this.props.yAxis.range[1] - this.props.yAxis.range[0];
    return (value * chartRangeY) / plotBkgHeight;
  };

  convertToPixels = (value) => {
    const plotBkgHeight = document
      .getElementById("swsHeatmap")
      .getElementsByClassName("highcharts-plot-background")[0]
      .getBoundingClientRect().height;

    const chartRangeY = this.props.yAxis.range[1] - this.props.yAxis.range[0];
    return (value * plotBkgHeight) / chartRangeY;
  };

  ////////////////////
  // HANDLE SLIDERS //
  ////////////////////

  // SWS COLORBAR RANGE SLIDER
  handleSwsColorbarChange = (e, newValue) => {
    if (
      newValue !==
      [
        Math.round(this.props.colorBar.yAxis.range[0]),
        Math.round(this.props.colorBar.yAxis.range[1]),
      ]
    ) {
      this.setState({ ...this.state, swsColorBarRange: newValue });
    }
  };

  commitSwsColorbarChange = (e) => {
    if (
      this.state.swsColorBarRange !==
      [
        Math.round(this.props.colorBar.yAxis.range[0]),
        Math.round(this.props.colorBar.yAxis.range[1]),
      ]
    ) {
      this.props.fetchSwsPlotAndVelocitySlicePlot(
        this.props.sample.id,
        this.state.swsColorBarRange
      );
    }
  };

  handleCancelSwsColorbarChange = (e) => {
    this.setState({
      ...this.state,
      swsColorBarRange: [
        Math.round(this.props.colorBar.yAxis.range[0]),
        Math.round(this.props.colorBar.yAxis.range[1]),
      ],
    });
  };

  commitGlobalSwsColorbarChange = (e) => {
    this.props.changeGlobalSwsColorbarChange(this.state.swsColorBarRange);
  };
  // B COLORBAR RANGE SLIDER
  handleBColorbarChange = (e, newValue) => {
    if (
      newValue !==
      [
        Math.round(this.props.colorBar.xAxis.range[0]),
        Math.round(this.props.colorBar.xAxis.range[1]),
      ]
    ) {
      this.setState({ ...this.state, bColorBarRange: newValue });
    }
  };

  commitBColorbarChange = (e) => {
    if (
      this.state.bColorBarRange !==
      [
        Math.round(this.props.colorBar.xAxis.range[0]),
        Math.round(this.props.colorBar.xAxis.range[1]),
      ]
    ) {
      this.props.fetchSwsPlot(this.props.sample.id, this.state.bColorBarRange);
    }
  };

  handleCancelBColorbarChange = (e) => {
    this.setState({
      bColorBarRange: [
        Math.round(this.props.colorBar.xAxis.range[0]),
        Math.round(this.props.colorBar.xAxis.range[1]),
      ],
    });
  };
  // VELOCITY COLORBAR RANGE SLIDER

  handleVelocityColorbarChange = (e, newValue) => {
    // move the slider
    if (newValue !== this.state.velocityColorbarValue) {
      this.setState({ velocityColorbarValue: newValue });
    }
  };

  commitVelocityColorbarChange = (event) => {
    if (
      this.state.velocityColorbarValue !==
      this.props.velocitySlicePlotColorBar.range[1]
    ) {
      this.props.setVMax(
        this.props.sample.id,
        this.state.velocityColorbarValue
      ); // change vMax redux value
      this.props.fetchSwsValueAndVelocitySlicePlot(this.props.sample.id);
    }
  };

  handleCancelVelocityColorbarChange = (e) => {
    this.setState({
      velocityColorbarValue: this.props.velocitySlicePlotColorBar.range[1],
    });
  };

  commitGlobalVelocityColorbarChange = (e) => {
    this.props.changeGlobalVelocityColorbarChange(
      this.state.velocityColorbarValue
    );
  };

  // ROI PERCENTAGE SLIDER
  handlePercentSliceSliderChange = (e, newValue) => {
    let newSliceWidth = this.props.sample.dz * newValue * 10;
    if (
      newValue !== this.state.sliceWidthSliderValue && // desired thickness changed
      newSliceWidth >= minRoiThickness // thickness is above minimum allowed
    ) {
      // move the slider
      this.setState({ sliceWidthSliderValue: newValue });

      // update DOM with slider width preview
      const plotBandROI = document.getElementsByClassName("plotBandROI")[0];
      const plotBandROIHeight = plotBandROI.getBoundingClientRect().height;
      const scaleToFull = this.calculateSliceThickness() / plotBandROIHeight;

      // scale to 100% and then scale again back by slider value
      // plotBandROI.style.transition = 'transform 0.2s ease' // animation disabled because of reset
      plotBandROI.style.transform = `scale(1, ${scaleToFull}) scale(1, ${
        newValue / 100
      })`;
    }
  };

  commitPercentSliceSliderChange = () => {
    if (this.state.sliceWidthSliderValue !== this.props.percentSliceWidth) {
      // Since the results were changed an existing roiReference should be set to null
      this.props.setRoiPercentageSlice(
        this.props.sample.id,
        this.state.sliceWidthSliderValue
      );
      this.resetRoiReference();
      this.props.fetchSwsValueAndVelocitySlicePlot(this.props.sample.id);
    }
  };

  handleCancelPercentSliceSliderChange = () => {
    this.setState({
      sliceWidthSliderValue: this.props.percentSliceWidth,
    });
  };

  /////////////////
  // OTHER STUFF //
  /////////////////

  handleApplyRoiToggle = (e) => {
    if (e.target.checked && this.props.sample.dz >= 0.0001) {
      this.setState({
        sliceWidthSliderValue: this.props.percentSliceWidth,
      });
      this.props.updateLocationWideRoiReference(
        this.props.sample.id,
        this.props.locationId
      );
    } else {
      this.props.updateLocationWideRoiReference(null, this.props.locationId);
    }
  };

  resetRoiReference = () => {
    // When the user modifies the results page, if there is an existing ROI reference it should be switched to null
    if (this.props.roiReference !== null) {
      this.props.updateLocationWideRoiReference(null, this.props.locationId);
    }
  };

  render() {
    const { classes } = this.props;

    // SWS PLOT
    // Update chart with values from redux store
    const chartOptionsSwsPlot = produce({ ...swsPlotOptions }, (draft) => {
      const sample = this.props.sample;

      // Chart
      draft.chart.plotBackgroundImage = this.props.backgroundImage;
      draft.chart.height = this.state.windowSwsPlotHeight; // resize chart based on screen size (rpi screen vs surface pro)
      draft.chart.width = this.state.windowSwsPlotWidth; //window.innerWidth > 900 ? 600 : 475; // resize chart based on screen size (rpi screen vs surface pro)

      // yAxis
      draft.yAxis.min = this.props.yAxis.range[0]; //yMin;
      draft.yAxis.max = this.props.yAxis.range[1];
      draft.yAxis.title.text = `${this.props.yAxis.label} (${this.props.yAxis.unit})`;
      draft.yAxis.labels.formatter = function () {
        let newValue = this.value;
        if (sample.calib !== null) {
          newValue -= sample.calib;
        }
        return `${newValue.toFixed(1)}`;
      };

      // plotLines
      draft.yAxis.plotLines[0].value = this.props.sliceStart; // line
      draft.yAxis.plotLines[1].value = this.props.sliceStart; // grabbable region top
      draft.yAxis.plotLines[2].value = this.props.sliceEnd; // line
      draft.yAxis.plotLines[3].value = this.props.sliceEnd; // grabbable region bottom

      // plotBand (center)
      draft.yAxis.plotBands[0].from = this.props.roiStart;
      draft.yAxis.plotBands[0].to = this.props.roiEnd;

      // plotband (top)
      // draft.yAxis.plotBands[1].from = this.props.yAxis.range[0];
      // draft.yAxis.plotBands[1].to =
      //   sample.calib !== null ? sample.calib : this.props.yAxis.range[0];

      // plotband (bottom)
      // draft.yAxis.plotBands[2].from =
      //   sample.calib !== null ? 2 * sample.calib : this.props.yAxis.range[1];
      // draft.yAxis.plotBands[2].to = this.props.yAxis.range[1];

      // xAxis
      if (this.props.xAxis.label === "") {
        draft.xAxis.min = null;
        draft.xAxis.max = null;
        draft.xAxis.title.text = null;
      } else {
        draft.xAxis.min = this.props.xAxis.range[0];
        draft.xAxis.max = this.props.xAxis.range[1];
        draft.xAxis.title.text = `${this.props.xAxis.label} (${this.props.xAxis.unit})`;
      }
    });

    // SWS PLOT - COLORBAR
    const colorBarOptions = produce({ ...swsPlotColorBarOptions }, (draft) => {
      if (this.props.xAxis.label === "") {
        draft.chart.height = this.state.windowSwsPlotHeight + 35; // 35 px for the difference in x-axis labels
      } else {
        draft.chart.height = this.state.windowSwsPlotHeight + 18; // 18 px for the difference in x-axis labels
      } // resize chart based on screen size (rpi screen vs surface pro)
      console.log(this.props.colorBar.xAxis.range);
      draft.xAxis.min = this.props.colorBar.xAxis.range[0];
      draft.xAxis.max = this.props.colorBar.xAxis.range[1];
      draft.xAxis.tickInterval =
        this.props.colorBar.xAxis.range[1] - this.props.colorBar.xAxis.range[0];
      console.log(
        this.props.colorBar.xAxis.range[1] - this.props.colorBar.xAxis.range[0]
      );
      draft.xAxis.title.text = `${this.props.colorBar.xAxis.unit}`;

      draft.yAxis.min = this.props.colorBar.yAxis.range[0];
      draft.yAxis.max = this.props.colorBar.yAxis.range[1];
      draft.yAxis.title.text = `${this.props.colorBar.yAxis.label} (${this.props.colorBar.yAxis.unit})`;
    });

    // VELOCITY SLICE PLOT
    let chartOptionsVelocitySlidePlot;
    if (this.props.velocitySlicePlot) {
      chartOptionsVelocitySlidePlot = produce(
        { ...velocitySlicePlotOptions(this.props.theme) },
        (draft) => {
          // Chart
          draft.chart.plotBackgroundImage =
            this.props.velocitySlicePlotBackgroundImage;
          draft.chart.height = this.state.windowVeloctiySlicePlotHeight; // resize chart based on screen size (rpi screen vs surface pro)
          draft.chart.width = this.state.windowVeloctiySlicePlotWidth; // window.innerWidth > 900 ? 600 : 475; // resize chart based on screen size (rpi screen vs surface pro)

          // yAxis
          draft.yAxis.min = this.props.velocitySlicePlotYAxis.range[0]; //yMin;
          draft.yAxis.max = this.props.velocitySlicePlotYAxis.range[1];
          draft.yAxis.title.text = `${this.props.velocitySlicePlotYAxis.label} (${this.props.velocitySlicePlotYAxis.unit})`;

          // xAxis
          draft.xAxis.min = this.props.velocitySlicePlotXAxis.range[0];
          draft.xAxis.max = this.props.velocitySlicePlotXAxis.range[1];
          draft.xAxis.title.text = `${this.props.velocitySlicePlotXAxis.label} (${this.props.velocitySlicePlotXAxis.unit})`;

          // Series
          draft.series[0].data = this.props.velocitySlicePlotData;
          draft.series[0].name = this.props.velocitySlicePlotXAxis.unit;
          draft.plotOptions.series.allowPointSelect =
            this.props.velocitySlicePlotAllowPointSelect;

          // Color bar
          draft.colorAxis.min = this.props.velocitySlicePlotColorBar.range[0];
          draft.colorAxis.max = this.props.velocitySlicePlotColorBar.range[1];
          draft.colorAxis.tickInterval =
            this.props.velocitySlicePlotColorBar.range[1];
          draft.legend.symbolHeight = this.state.colorBarVeloctiySliceHeight;
        }
      );
    }
    return (
      <React.Fragment>
        {/* TITLE */}
        <Grid item className={classes.graphTitleContainer}>
          <Typography className={classes.graphTitle}>
            {`${this.props.acqTypes["CSI"]["name"]} Sample ${this.props.sampleCount}/${this.props.totalSamples}`}
          </Typography>
        </Grid>
        {/* CHART */}
        <Grid item container wrap="nowrap">
          <Grid
            item
            container
            className={classes.graphContainer}
            justify="center"
            id="swsHeatmap"
          >
            <HighchartsReact
              highcharts={Highcharts}
              ref={this.chartRefSwsPlot}
              options={chartOptionsSwsPlot}
            />
          </Grid>

          <Grid
            item
            container
            className={classes.graphContainer}
            justify="center"
          >
            <HighchartsReact
              highcharts={Highcharts}
              options={colorBarOptions}
            />
          </Grid>
        </Grid>
        {this.props.velocitySlicePlot && (
          <Grid
            item
            container
            wrap="nowrap"
            className={classes.graphContainer}
            justify="center"
            id="velocitySliceHeatmap"
          >
            <HighchartsReact
              highcharts={Highcharts}
              ref={this.chartRefVelocitySlicePlot}
              options={chartOptionsVelocitySlidePlot}
            />
          </Grid>
        )}
        {/* ALERT MESSAGE */}
        {this.props.sample.alert.state && (
          <Grid item container wrap="nowrap" className={classes.alertGrid}>
            <ListItem>
              <ListItemIcon>
                <WarningIcon className={classes.listIcon} />
              </ListItemIcon>
              <ListItemText
                className={classes.listText}
                primary={this.props.sample.alert.message}
              />
            </ListItem>
          </Grid>
        )}

        {/* CONTROLS */}
        <Grid
          item
          container
          wrap="nowrap"
          className={classes.graphControlsContainer}
        >
          <Accordion
            className={classes.accordian}
            expanded={this.state.expandGraphSettings}
            onChange={() =>
              this.setState({
                ...this.state,
                expandGraphSettings: !this.state.expandGraphSettings,
              })
            }
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography>Image Settings</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Grid
                item
                container
                direction="row"
                className={classes.graphSettingsGrid}
              >
                <Divider className={classes.divider} />
                <ListSubheader
                  disableSticky
                  className={classes.graphSettingsSubheader}
                >
                  ROI Settings
                </ListSubheader>
                <Grid item container className={classes.graphSettingsSubGrid}>
                  {/* SLICE WIDTH */}
                  <Grid
                    item
                    container
                    alignItems="center"
                    className={classes.sliderDiv}
                  >
                    <Typography className={classes.sliderTitle}>
                      ROI:
                    </Typography>
                    <Slider
                      className={classes.sliderContainer}
                      value={this.state.sliceWidthSliderValue}
                      step={10}
                      min={10}
                      max={100}
                      aria-labelledby="discrete-slider-always"
                      valueLabelFormat={(value) => `${value}%`}
                      marks={[
                        {
                          value: 10,
                          label: "10%",
                        },
                        {
                          value: 20,
                        },
                        {
                          value: 30,
                        },
                        {
                          value: 40,
                        },
                        {
                          value: 50,
                          label: "50%",
                        },
                        {
                          value: 60,
                        },
                        {
                          value: 70,
                        },
                        {
                          value: 80,
                        },
                        {
                          value: 90,
                        },
                        {
                          value: 100,
                          label: "100%",
                        },
                      ]}
                      valueLabelDisplay="on"
                      disabled={!this.props.enabled}
                      onChange={this.handlePercentSliceSliderChange}
                    />
                  </Grid>

                  <Grid item container className={classes.colorbarButtons}>
                    <Button
                      onClick={this.handleCancelPercentSliceSliderChange}
                      disabled={!this.props.enabled}
                      className={classes.buttonOutlined}
                      variant="outlined"
                      color="primary"
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={this.commitPercentSliceSliderChange}
                      disabled={
                        !this.props.enabled ||
                        this.state.sliceWidthSliderValue ===
                          this.props.percentSliceWidth
                      }
                      className={classes.buttonPrimary}
                      variant="contained"
                      color="primary"
                    >
                      Apply
                    </Button>

                    <FormControlLabel
                      key={this.props.sample.id}
                      label={
                        <div>
                          <div>Apply ROI to all</div>
                          <div>{`${this.props.acqTypes.CSI.name} samples`}</div>
                        </div>
                      }
                      className={classes.switch}
                      control={
                        <Switch
                          checked={
                            this.props.roiReference === this.props.sample.id
                          }
                          disabled={
                            true
                            // ( && !this.props.enabled) ||
                            // !this.props.samplesDoneFlag
                          }
                          onChange={this.handleApplyRoiToggle}
                          color="primary"
                        />
                      }
                    />
                  </Grid>
                </Grid>

                <Divider className={classes.divider} />
                <ListSubheader
                  disableSticky
                  className={classes.graphSettingsSubheader}
                >
                  Colorbar Settings
                </ListSubheader>
                {/* Controls */}
                <Grid
                  item
                  container
                  wrap="nowrap"
                  className={classes.graphSettingsSubGrid}
                >
                  <Grid
                    item
                    container
                    alignItems="center"
                    className={classes.sliderDiv}
                  >
                    <Typography className={classes.sliderTitle}>
                      {this.props.colorBar.yAxis.label}:
                    </Typography>
                    <Slider
                      value={this.state.swsColorBarRange}
                      className={classes.sliderContainer}
                      step={1}
                      min={1}
                      max={10}
                      aria-labelledby="discrete-slider-always"
                      valueLabelFormat={(value) =>
                        `${value}${this.props.colorBar.yAxis.unit}`
                      }
                      marks={[
                        {
                          value: 1,
                          label: `1 ${this.props.colorBar.yAxis.unit}`,
                        },
                        {
                          value: 2,
                        },
                        {
                          value: 3,
                        },
                        {
                          value: 4,
                        },
                        {
                          value: 5,
                          label: `5 ${this.props.colorBar.yAxis.unit}`,
                        },
                        {
                          value: 6,
                        },
                        {
                          value: 7,
                        },
                        {
                          value: 8,
                        },
                        {
                          value: 9,
                        },
                        {
                          value: 10,
                          label: `10 ${this.props.colorBar.yAxis.unit}`,
                        },
                      ]}
                      valueLabelDisplay="on"
                      disabled={
                        true
                        // && !this.props.enabled
                      }
                      onChange={this.handleSwsColorbarChange}
                    />
                  </Grid>

                  <Grid item container className={classes.colorbarButtons}>
                    <Button
                      onClick={this.handleCancelSwsColorbarChange}
                      disabled={true && !this.props.enabled}
                      className={classes.buttonOutlined}
                      variant="outlined"
                      color="primary"
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={this.commitSwsColorbarChange}
                      disabled={
                        true
                        //  !this.props.enabled ||
                        // (this.state.swsColorBarRange[0] ===
                        //   Math.round(this.props.colorBar.yAxis.range[0]) &&
                        //   this.state.swsColorBarRange[1] ===
                        //     Math.round(this.props.colorBar.yAxis.range[1])) ||
                        // this.state.swsColorBarRange[0] ===
                        //   this.state.swsColorBarRange[1]
                      }
                      className={classes.buttonPrimary}
                      variant="contained"
                      color="primary"
                    >
                      Apply
                    </Button>
                    <Button
                      onClick={this.commitGlobalSwsColorbarChange}
                      disabled={
                        true
                        // ( !this.props.enabled) ||
                        // !this.props.samplesDoneFlag ||
                        // this.state.swsColorBarRange[0] ===
                        //   this.state.swsColorBarRange[1]
                      }
                      className={classes.buttonPrimary}
                      variant="contained"
                      color="primary"
                    >
                      Apply to all images
                    </Button>
                  </Grid>
                </Grid>

                <Grid
                  item
                  container
                  wrap="nowrap"
                  className={classes.graphSettingsSubGrid}
                >
                  <Grid
                    item
                    container
                    alignItems="center"
                    className={classes.sliderDiv}
                  >
                    <Typography className={classes.sliderTitle}>
                      {this.props.colorBar.xAxis.label}:
                    </Typography>
                    <Slider
                      value={this.state.bColorBarRange}
                      className={classes.sliderContainer}
                      step={10}
                      min={-60}
                      max={0}
                      aria-labelledby="discrete-slider-always"
                      valueLabelFormat={(value) =>
                        `${value}${this.props.colorBar.xAxis.unit}`
                      }
                      marks={[
                        {
                          value: -60,
                          label: `-60 ${this.props.colorBar.xAxis.unit}`,
                        },

                        {
                          value: -50,
                        },
                        {
                          value: -40,
                          label: `40 ${this.props.colorBar.xAxis.unit}`,
                        },
                        {
                          value: -30,
                        },
                        {
                          value: -20,
                          label: `20 ${this.props.colorBar.xAxis.unit}`,
                        },
                        {
                          value: -10,
                        },
                        {
                          value: 0,
                          label: `0 ${this.props.colorBar.xAxis.unit}`,
                        },
                      ]}
                      valueLabelDisplay="on"
                      disabled={!this.props.enabled}
                      onChange={this.handleBColorbarChange}
                    />
                  </Grid>

                  <Grid item container className={classes.colorbarButtons}>
                    <Button
                      onClick={this.handleCancelBColorbarChange}
                      disabled={!this.props.enabled}
                      className={classes.buttonOutlined}
                      variant="outlined"
                      color="primary"
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={this.commitBColorbarChange}
                      disabled={
                        !this.props.enabled ||
                        (this.state.bColorBarRange[0] ===
                          Math.round(this.props.colorBar.xAxis.range[0]) &&
                          this.state.bColorBarRange[1] ===
                            Math.round(this.props.colorBar.xAxis.range[1])) ||
                        this.state.bColorBarRange[0] ===
                          this.state.bColorBarRange[1]
                      }
                      className={classes.buttonPrimary}
                      variant="contained"
                      color="primary"
                    >
                      Apply
                    </Button>
                  </Grid>
                </Grid>

                {this.props.velocitySlicePlot && (
                  <Grid
                    item
                    container
                    wrap="nowrap"
                    className={classes.graphSettingsSubGrid}
                  >
                    <Grid
                      item
                      container
                      className={classes.sliderDiv}
                      alignItems="center"
                    >
                      <Typography className={classes.sliderTitle}>
                        {this.props.velocitySlicePlotColorBar.label}:
                      </Typography>
                      <Slider
                        value={this.state.velocityColorbarValue}
                        step={2}
                        min={2}
                        max={30}
                        className={classes.sliderContainer}
                        valueLabelFormat={(value) =>
                          `${value}${this.props.velocitySlicePlotColorBar.unit}`
                        }
                        marks={[
                          {
                            value: 2,
                            label: `2 ${this.props.velocitySlicePlotColorBar.unit}`,
                          },
                          {
                            value: 4,
                          },
                          {
                            value: 6,
                          },
                          {
                            value: 8,
                          },
                          {
                            value: 10,
                          },
                          {
                            value: 12,
                          },

                          {
                            value: 14,
                          },
                          {
                            value: 16,
                            label: `16 ${this.props.velocitySlicePlotColorBar.unit}`,
                          },
                          {
                            value: 18,
                          },
                          {
                            value: 20,
                          },
                          {
                            value: 22,
                          },
                          {
                            value: 24,
                          },
                          {
                            value: 26,
                          },
                          {
                            value: 28,
                          },
                          {
                            value: 30,
                            label: `30 ${this.props.velocitySlicePlotColorBar.unit}`,
                          },
                        ]}
                        valueLabelDisplay="on"
                        disabled={!this.props.enabled}
                        onChange={this.handleVelocityColorbarChange}
                      />{" "}
                    </Grid>

                    <Grid item container className={classes.colorbarButtons}>
                      <Button
                        onClick={this.handleCancelVelocityColorbarChange}
                        disabled={!this.props.enabled}
                        className={classes.buttonOutlined}
                        variant="outlined"
                        color="primary"
                      >
                        Cancel
                      </Button>
                      <Button
                        onClick={this.commitVelocityColorbarChange}
                        disabled={
                          !this.props.enabled ||
                          this.state.velocityColorbarValue ===
                            Math.round(
                              this.props.velocitySlicePlotColorBar.range[1]
                            )
                        }
                        className={classes.buttonPrimary}
                        variant="contained"
                        color="primary"
                      >
                        Apply
                      </Button>
                      {/* <Button
                        onClick={this.commitGlobalVelocityColorbarChange}
                        disabled={
                          !this.props.enabled || !this.props.samplesDoneFlag
                        }
                        className={classes.buttonPrimary}
                        variant="contained"
                        color="primary"
                      >
                        Apply to all images
                      </Button> */}
                    </Grid>
                  </Grid>
                )}
              </Grid>
            </AccordionDetails>
          </Accordion>
        </Grid>
      </React.Fragment>
    );
  }
}

const mapDispatchToProps = (dispatch) => {
  return bindActionCreators(
    {
      setZValue,
      fetchSwsValueAndVelocitySlicePlot,
      fetchSwsPlotAndVelocitySlicePlot,
      setDz,
      setRoiPercentageSlice,
      updateLocationWideRoiReference,
      fetchSwsPlot,
      setVMax,
      changeGlobalVelocityColorbarChange,
      changeGlobalSwsColorbarChange,
    },
    dispatch
  );
};

const mapStateToProps = (state) => {
  let sample = state.samples.byId[state.samples.selectedSampleId];
  let z = sample.zValue;

  // Graph Title
  const locationSamples =
    state.locations.byId[state.locations.selectedLocationId].samples;
  let locationCsiSamples = [];
  for (let locationSampleId of locationSamples) {
    if (csiTypes.includes(state.samples.byId[locationSampleId].type)) {
      locationCsiSamples.push(locationSampleId);
    }
  }

  // Velocity Slice Plot data only if CSI sample is STL or MMODE
  let veloctiySlicePlotVariables = {};
  if (["STL", "MMODE"].includes(sample.type)) {
    veloctiySlicePlotVariables = {
      velocitySlicePlot: ["STL", "MMODE"].includes(sample.type),
      velocitySlicePlotBackgroundImage: sample.velocitySlicePlot.image,
      velocitySlicePlotXAxis: sample.velocitySlicePlot.xAxis,
      velocitySlicePlotYAxis: sample.velocitySlicePlot.yAxis,
      velocitySlicePlotColorBar: sample.velocitySlicePlot.colorBar,
      velocitySlicePlotData: JSON.parse(
        JSON.stringify(sample.velocitySlicePlot.peakData)
      ), // Need to "copy" the object to prevent mutating the original one from redux!
      velocitySlicePlotAllowPointSelect: !sample.processing && !sample.locked,
    };
  }

  let zList = Array(sample.zList.nz)
    .fill()
    .map((_, idx) => sample.zList.z0 + sample.zList.inc * idx);

  const enabled =
    state.studyInfo.id === studyFixture.study.studyInfo.id ? false : true;

  // !sample.processing &&
  // !state.studyInfo.locked &&
  // sample.dataAvailable &&
  // !state.device.acquiring;

  return {
    acqTypes: state.protocol.acq_types,
    sample: sample,
    enabled: enabled,
    locked: state.studyInfo.locked,
    roiReference:
      state.locations.byId[state.locations.selectedLocationId].roiReference
        .locationWide.id, // id of master sample for roi ref (if enabled).
    locationId: state.locations.selectedLocationId,
    zList: zList,
    backgroundImage: sample.swsPlot.image,
    xAxis: sample.swsPlot.xAxis,
    yAxis: sample.swsPlot.yAxis,
    colorBar: sample.swsPlot.colorBar,
    percentSliceWidth: sample.percentSliceWidth, // 0-100
    sliceStart: z - sample.dz / 2, // the greater 'slice' region
    sliceEnd: z + sample.dz / 2,
    roiStart: z - sample.roiDz / 2, // the Region Of Interest that data is used from (smaller 'slice' region)
    roiEnd: z + sample.roiDz / 2,
    sampleCount: locationCsiSamples.indexOf(state.samples.selectedSampleId) + 1,
    totalSamples: locationCsiSamples.length,
    samplesDoneFlag: state.utility.samplesDone,
    ...veloctiySlicePlotVariables,
    theme: state.utility.theme,
  };
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withStyles(styles)(CsiPlot));
